import React from 'react';
import PropTypes from 'prop-types';

import { Thumbnail } from '../Thumbnail';
import { useDynamicMaxHeight } from '../../hooks/useDynamicMaxHeight';
import { getDCEPhaseGroups, DCEPhaseGroup, ThumbnailItem } from '../../utils/getDCEPhaseGroups';

const ThumbnailList = ({
  thumbnails,
  onThumbnailClick,
  onThumbnailDoubleClick,
  onClickUntrack,
  activeDisplaySetInstanceUIDs = [],
  viewPreset,
  ThumbnailMenuItems,
  onEditDescription,
  orientation = 'vertical',
}) => {
  // Use the dynamic height hook on the parent container
  const { ref, maxHeight } = useDynamicMaxHeight(thumbnails);

  const isAcrPacs = (window as any).ENV_CONFIG?.IS_ACR_PACS === true;

  const { dceGroups, ungrouped } = isAcrPacs
    ? getDCEPhaseGroups(thumbnails ?? [])
    : { dceGroups: [] as DCEPhaseGroup[], ungrouped: (thumbnails ?? []) as ThumbnailItem[] };

  // Helper to render a single thumbnail row
  const renderThumbnailItem = (item: ThumbnailItem & { [key: string]: any }, forceViewPreset?: string) => {
    const { displaySetInstanceUID, componentType, numInstances, loadingProgress, ...rest } = item;
    const isActive = activeDisplaySetInstanceUIDs.includes(displaySetInstanceUID);
    const usedViewPreset = forceViewPreset ?? viewPreset;

    return (
      <div key={displaySetInstanceUID} className={isHorizontal ? 'flex-shrink-0 h-full' : 'w-full'}>
        <Thumbnail
          {...rest}
          loadingProgress={loadingProgress}
          displaySetInstanceUID={displaySetInstanceUID}
          numInstances={numInstances || 1}
          isActive={isActive}
          thumbnailType={componentType}
          viewPreset={usedViewPreset}
          orientation={orientation}
          onClick={onThumbnailClick.bind(null, displaySetInstanceUID)}
          onDoubleClick={onThumbnailDoubleClick.bind(null, displaySetInstanceUID)}
          onClickUntrack={onClickUntrack.bind(null, displaySetInstanceUID)}
          ThumbnailMenuItems={ThumbnailMenuItems}
          onEditDescription={() => onEditDescription?.(item)}
        />
      </div>
    );
  };

  // Filter ungrouped thumbnails into list/thumbnail buckets (same as before)
  const listItems = ungrouped?.filter(
    ({ componentType }) => componentType === 'thumbnailNoImage' || viewPreset === 'list'
  );

  const thumbnailItems = ungrouped?.filter(
    ({ componentType }) => componentType !== 'thumbnailNoImage' && viewPreset === 'thumbnails'
  );

  const isHorizontal = orientation === 'horizontal';

  return (
    <div className={isHorizontal ? 'flex flex-row h-full flex-nowrap' : 'flex flex-col'}>
      <div
        ref={ref}
        className={isHorizontal
          ? 'flex flex-row flex-nowrap gap-[0.25rem] px-[0.25rem] py-[0.125rem] h-full items-start'
          : 'flex flex-col gap-[0.125rem] pt-[0.125rem] pr-[0.25rem] pl-[0.25rem] pb-[0.125rem]'
        }
      >
        {/* DCE Phase Groups (only when IS_ACR_PACS) */}
        {dceGroups.map((group: DCEPhaseGroup) => (
          <div key={group.groupLabel} className="flex flex-col mb-[0.5rem]">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-[0.125rem] pt-[0.25rem] pb-[0.125rem] border-b border-border">
              {group.groupLabel}
            </div>
            {group.phases.map(item => (
              <div key={item.displaySetInstanceUID} className="flex flex-col gap-[0.0625rem] mt-[0.25rem]">
                <div className="text-[10px] text-blue-400 px-[2px]">{item.phaseLabel}</div>
                {renderThumbnailItem(item, 'thumbnails')}
              </div>
            ))}
          </div>
        ))}

        {/* Regular thumbnail grid (ungrouped) */}
        {thumbnailItems?.length > 0 && (
          <div
            id="ohif-thumbnail-list"
            className={isHorizontal
              ? 'bg-bkg-low flex flex-row flex-nowrap gap-[0.25rem] items-start'
              : 'bg-bkg-low flex flex-col gap-[0.5rem]'
            }
          >
            {thumbnailItems.map(item => renderThumbnailItem(item))}
          </div>
        )}

        {/* List Items (ungrouped) */}
        {listItems?.length > 0 && (
          <div
            id="ohif-thumbnail-list"
            className={isHorizontal
              ? 'bg-bkg-low flex flex-row flex-nowrap gap-[0.25rem] items-start'
              : 'bg-bkg-low grid grid-cols-[repeat(auto-fit,_minmax(0,275px))] place-items-start gap-[0.5rem]'
            }
          >
            {listItems.map(item => renderThumbnailItem(item))}
          </div>
        )}
      </div>
    </div>
  );
};

ThumbnailList.propTypes = {
  thumbnails: PropTypes.arrayOf(
    PropTypes.shape({
      displaySetInstanceUID: PropTypes.string.isRequired,
      imageSrc: PropTypes.string,
      imageAltText: PropTypes.string,
      seriesDate: PropTypes.string,
      seriesNumber: PropTypes.any,
      numInstances: PropTypes.number,
      description: PropTypes.string,
      componentType: PropTypes.any,
      isTracked: PropTypes.bool,
      /**
       * Data the thumbnail should expose to a receiving drop target. Use a matching
       * `dragData.type` to identify which targets can receive this draggable item.
       * If this is not set, drag-n-drop will be disabled for this thumbnail.
       *
       * Ref: https://react-dnd.github.io/react-dnd/docs/api/use-drag#specification-object-members
       */
      dragData: PropTypes.shape({
        /** Must match the "type" a dropTarget expects */
        type: PropTypes.string.isRequired,
      }),
    })
  ),
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  onThumbnailClick: PropTypes.func.isRequired,
  onThumbnailDoubleClick: PropTypes.func.isRequired,
  onClickUntrack: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  ThumbnailMenuItems: PropTypes.any,
  onEditDescription: PropTypes.func,
  orientation: PropTypes.string,
};

export { ThumbnailList };
